import { useState, useEffect } from 'react'
import { t } from '@lingui/core/macro'
import CloseIcon from '@mui/icons-material/Close'
import AppBar from '@mui/material/AppBar'
import Button from '@mui/material/Button'
import Drawer from '@mui/material/Drawer'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { func, bool, string, number, oneOfType } from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { Paper, CvsAutoComplete, ErrorDialog } from '@components'
import { colors } from '@global/constants'
import { api } from 'api'
import { ValidationConfigurator } from './validation-configurator'
import * as TemplateActions from '../template.actions'
import { UrlConstruction } from './url-construction'

const linkErrors = () => [
  {
    title: t`Field Name Missing`,
    description: t`The field name is required`,
    type: t`Field Name`,
    errorActive: false,
  },
  {
    title: t`Link Parameter Errors`,
    description: t`There are errors in your link parameter configuration`,
    type: t`Link Parameters`,
    errorActive: false,
  },
  {
    title: t`Link Validation Configuration Errors`,
    description: t`There are errors in your link validation configuration`,
    type: t`Link Validations`,
    errorActive: false,
  },
]

export const EditLink = ({ onClose = () => {}, visible = false, title = '', fieldId = '' }) => {
  const [accounts, setAccounts] = useState([])
  const [types, setTypes] = useState([])
  const [originalLink, setOriginalLink] = useState(null)
  const [originalValidations, setOriginalValidations] = useState([])
  const [originalParams, setOriginalParams] = useState([])
  const [saveEnabled, setSaveEnabled] = useState(true)
  const [showErrors, setShowErrors] = useState(false)
  const [errors, setErrors] = useState(linkErrors())
  const linkParams = useSelector(state => state.Template.parameters)
  const templateId = useSelector(state => state.Template.template.id)
  const savingTemplate = useSelector(state => state.Template.savingTemplate)
  const [configurationErrorsModalOpen, setConfigurationErrorsModalOpen] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    const call = async () => {
      const { data } = await api.get('/api/v1/account/connector')
      setAccounts(
        data.map(acct => ({ id: acct.id, slug: acct.connector_type.slug, name: acct.name })),
      )
    }
    call()
  }, [])

  useEffect(() => {
    setSaveEnabled(!errors.filter(e => e.errorActive).length)
  }, [errors.filter(e => e.errorActive).length])

  const LINK_VALIDATION_DISABLED = useSelector(
    state => state.Auth.features?.['disable-link-validations'],
  )

  useEffect(() => {
    checkParameters()
  }, [linkParams])

  const linkField = useSelector(state =>
    Object.values(state.Template.templateFieldsById).find(
      tf => tf.governance_type === 'link' || (tf.field && tf.field.governance_type === 'link'),
    ),
  )

  const fields = useSelector(state => Object.values(state.Template.fieldsById)).filter(
    f => f && f.governance_type !== 'link',
  )
  const autofillField = linkField.autofill?.field_id ? linkField.autofill.field_id : null

  const currentError = errors.filter(e => e.errorActive)

  useEffect(() => {
    api.get('/api/v1/link-validation/types').then(res => {
      setTypes(res.data)
    })
    // Check that we aren't using a TEMP_UUID field ID
    if ((fieldId && !isNaN(fieldId)) || fieldId === 0) {
      api.get(`/api/v1/account/template/template-field/${fieldId}`).then(res => {
        setOriginalLink(res.data)
      })
    }
    if (templateId || templateId === 0) {
      api
        .get(`/api/v1/account/template/link-validation`, {
          params: {
            templateId,
          },
        })
        .then(res => {
          setOriginalValidations(res.data)
        })
    }
    if (templateId || templateId === 0) {
      api.get(`/api/v1/account/template/${templateId}/parameters`).then(res => {
        setOriginalParams(res.data)
      })
    }
  }, [savingTemplate])

  const updateName = e => {
    // todo need to save this at some point. Dismount?
    e.target.value === '' &&
      setErrors(
        errors.map(error =>
          error.type === 'Field Name' ? { ...error, errorActive: true } : error,
        ),
      )
    e.target.value !== '' &&
      errors.filter(error => error.type === 'Field Name' && error.errorActive).length > 0 &&
      setErrors(
        errors.map(error =>
          error.type === 'Field Name' ? { ...error, errorActive: false } : error,
        ),
      )
    dispatch(
      TemplateActions.updateTemplateField({
        ...linkField,
        field: { ...linkField.field, name: e.target.value },
      }),
    )
  }

  const getParams = () =>
    originalParams.map(param => {
      const {
        fieldset_ids,
        template_field,
        id,
        name,
        precedence,
        template_id,
        transform,
        url_encoding,
        url_name_encodings,
      } = param

      return {
        field_id: template_field?.field_id,
        fieldset_ids,
        id,
        name,
        precedence,
        template_id,
        transform,
        url_encoding,
        url_name_encoding: url_name_encodings,
      }
    })

  const discardChanges = () => {
    dispatch(TemplateActions.resetValidations(originalValidations))
    dispatch(TemplateActions.updateTemplateField({ ...originalLink }))
    dispatch(TemplateActions.setParameters(getParams()))
    setErrors(errors.map(error => ({ ...error, errorActive: false })))
    setConfigurationErrorsModalOpen(false)
    onClose()
  }

  const setAutofill = val => {
    const newAutofillGovernance = val
      ? {
          field_id: val,
          governable_id: linkField.id,
          governable_type: 'template_field',
          id: null,
        }
      : null
    dispatch(
      TemplateActions.updateTemplateField({
        ...linkField,
        autofill: newAutofillGovernance,
      }),
    )
  }

  const getActionButtons = type => {
    switch (type) {
      case 'Field Name':
        return (
          <>
            <Button color="primary" onClick={() => setConfigurationErrorsModalOpen(false)}>
              {t`Cancel`}
            </Button>
            <Button variant="outlined" color="primary" onClick={discardChanges}>
              {t`Discard changes`}
            </Button>
          </>
        )
      default:
        return (
          <>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setShowErrors(true)
                setConfigurationErrorsModalOpen(false)
              }}
            >
              {t`Show errors`}
            </Button>
            <Button variant="outlined" color="primary" onClick={discardChanges}>
              {t`Discard changes`}
            </Button>
          </>
        )
    }
  }

  const checkParameters = () => {
    let noErrors = true
    linkParams.forEach(param => {
      if (param.field_id === null || param.name.trim() === '') {
        noErrors = false
        setErrors(errors.map(e => (e.type === 'Link Parameters' ? { ...e, errorActive: true } : e)))
      }
    })
    noErrors &&
      errors.filter(e => e.type === 'Link Parameters' && e.errorActive).length > 0 &&
      setErrors(errors.map(e => (e.type === 'Link Parameters' ? { ...e, errorActive: false } : e)))
  }

  const handleClose = async () => {
    saveEnabled ? onClose() : setConfigurationErrorsModalOpen(true)
  }

  return (
    <Drawer anchor="right" open={Boolean(visible)} onClose={handleClose}>
      {visible && (
        <div style={{ width: '90vw', backgroundColor: colors.gray.light, height: '100%' }}>
          <AppBar
            position="relative"
            style={{ padding: 24, display: 'flex', flexDirection: 'row' }}
          >
            <Typography variant="h6" style={{ color: 'white' }}>
              {title}
            </Typography>
            <div style={{ flex: 1, textAlign: 'right' }}>
              <CloseIcon style={{ marginTop: 4, cursor: 'pointer' }} onClick={handleClose} />
            </div>
          </AppBar>
          <div style={{ padding: 24 }}>
            <Typography variant="h6">{t`Link definition`}</Typography>
            <Paper elevation={1} style={{ marginBottom: 12, padding: 12 }}>
              <div>
                <TextField
                  label={t`Field name`}
                  defaultValue={linkField.field.name}
                  onBlur={updateName}
                  variant="outlined"
                  margin="dense"
                />
              </div>
              <div style={{ width: 194 }}>
                <CvsAutoComplete
                  label={t`Autofill link`}
                  margin="dense"
                  options={fields.map(f => ({
                    label: f.name,
                    value: f.id,
                  }))}
                  onSelected={setAutofill}
                  isClearable
                  selected={autofillField}
                />
              </div>
            </Paper>
            <UrlConstruction
              resetIsOpen={() => setShowErrors(false)}
              isOpen={
                showErrors &&
                errors.filter(e => e.type === 'Link Parameters' && e.errorActive).length > 0
              }
            />
            {!LINK_VALIDATION_DISABLED && (
              <div>
                <ValidationConfigurator
                  types={types}
                  accounts={accounts}
                  saveEnabled={
                    !errors.filter(e => e.type === 'Link Validations' && e.errorActive).length
                  }
                  updateErrorStatus={value =>
                    setErrors(
                      errors.map(e =>
                        e.type === 'Link Validations' ? { ...e, errorActive: value } : e,
                      ),
                    )
                  }
                  showErrors={showErrors}
                  resetShowErrors={() => setShowErrors(false)}
                />
                <div style={{ width: '100%' }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleClose}
                    style={{ width: '100px', marginTop: '20px' }}
                  >
                    {t`Done`}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {currentError.length ? (
        <ErrorDialog
          isOpen={!!configurationErrorsModalOpen}
          onClose={() => {
            setConfigurationErrorsModalOpen(false)
          }}
          error={currentError[0]}
          actionButtons={<>{getActionButtons(currentError[0].type)}</>}
        />
      ) : null}
    </Drawer>
  )
}

EditLink.propTypes = {
  onClose: func,
  visible: bool,
  title: string,
  fieldId: oneOfType([string, number]).isRequired,
}
